import { api } from "@api";
import { logEvent } from "@api/session";
import { addHours, format } from "date-fns";
import moment from "moment";
import { createSignal, onMount, Show } from "solid-js";
import { CountdownTimer } from "./CountdownTimer";

const isDST = (d: Date) => {
  let jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset();
  let jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset();
  return Math.max(jan, jul) !== d.getTimezoneOffset();
};

export const GrandEventCard = () => {
  const [eventName, setEventName] = createSignal("");
  const [prizeMoney, setPrizeMoney] = createSignal("-");
  const [date, setDate] = createSignal("");
  const [city, setCity] = createSignal("");
  const [state, setState] = createSignal("");
  const [days, setDays] = createSignal(0);
  const [hours, setHours] = createSignal(0);
  const [minutes, setMinutes] = createSignal(0);
  const [seconds, setSeconds] = createSignal(0);
  const [loading, setLoading] = createSignal(true);
  const [eventId, setEventId] = createSignal(0);
  const [start, setStart] = createSignal(0);
  const getLive = () => new Date().getTime() > start();

  const loadEvent = async () => {
    setLoading(true);
    const res = (await api.events.get({})).events?.filter(
      (e) => !e.private
    )?.[0];
    console.log("received", res);
    if (!res) {
      console.log("no event");
      setLoading(false);
      return;
    }
    setEventName(res.name);
    setPrizeMoney(
      res.currentJackpot
        .toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })
        .slice(0, -3)
    );
    setDate(format(new Date(res.startTime), "MMM d, yyyy"));

    const refDate = new Date();
    const eDate = new Date(res.startTime);
    const isTodayDST = isDST(refDate);
    const isStartDST = isDST(eDate);

    const hourOffset = isTodayDST === isStartDST ? 0 : isTodayDST ? 1 : -1;

    const todayDate = new Date(addHours(refDate, hourOffset)).getTime();
    const start = eDate.getTime();
    setStart(start);

    const days = Math.max(moment(start).diff(todayDate, "days"), 0);
    const hours = Math.max(
      moment(start).diff(todayDate, "hours") - 24 * days,
      0
    );
    const minutes = Math.max(
      moment(start).diff(todayDate, "minutes") - (60 * 24 * days + 60 * hours),
      0
    );
    const seconds = Math.max(
      moment(start).diff(todayDate, "seconds") -
        (60 * 60 * 24 * days + 60 * 60 * hours + 60 * minutes),
      0
    );
    setDays(days);
    setHours(hours);
    setMinutes(minutes);
    setSeconds(seconds);
    setLoading(false);
    setEventId(res.id);
    setCity(res.city);
    setState(res.state);
  };

  onMount(() => {
    loadEvent();
  });

  return (
    <div class="w-[350px] bg-white rounded-xl z-10 p-4 my-8 lg:mb-0">
      <Show
        when={!(!loading() && eventName() === "")}
        fallback={
          <>
            <Show
              when={!loading()}
              fallback={
                <div class="flex m-4 justify-center items-center">
                  <span class="loading loading-spinner loading-md mr-2"></span>
                </div>
              }
            >
              <h2 class="text-3xl font-bold text-primary w-full flex justify-center align-middle items-center">
                Stay tuned!
              </h2>
              <h3 class="text-xl font-bold text-primary text-center w-full mb-1">
                Events coming soon!
              </h3>
            </Show>
          </>
        }
      >
        <Show
          when={!loading()}
          fallback={
            <div class="flex m-4 justify-center items-center">
              <span class="loading loading-spinner loading-md mr-2"></span>
            </div>
          }
        >
          <p class="text-text-secondary w-full mb-1">
            {date()} - {city()}, {state()} USA
          </p>
          <h2
            class="text-3xl text-center font-bold text-primary w-full no-underline link"
            onclick={() =>
              (window.location.href = `/event?eventId=${eventId()}`)
            }
          >
            {eventName()}
          </h2>

          <h3 class="text-xl font-bold text-primary text-center w-full mb-1">
            Minimum Cash Jackpot
          </h3>
          <h1 class="text-6xl font-bold text-primary text-center w-full">
            {prizeMoney()}
          </h1>
          <Show
            when={getLive()}
            fallback={
              <p class="text-primary w-full text-xl font-semibold mt-3">
                Event begins in
              </p>
            }
          >
            <p class="text-primary w-full text-xl font-semibold mt-3">
              Event is live!
            </p>
          </Show>
          <div class="pb-6">
            <CountdownTimer
              days={days()}
              hours={hours()}
              minutes={minutes()}
              seconds={seconds()}
            />
          </div>
          <div class="w-full justify-center flex">
            <a
              class="btn btn-primary flex-shrink"
              classList={{
                "btn-disabled": loading(),
              }}
              href={`/event-registration/?eventId=${eventId()}`}
              onClick={() => {
                // TODO - consider moving these to on mount of registration wizard
                logEvent({
                  eventType: "action",
                  data: {
                    eventId: eventId().toString(),
                    action: "click-register-team",
                  },
                });
              }}
            >
              {"Register Team"}
            </a>
            <span class="mx-4 font-bold self-center flex-shrink"> OR </span>
            <a
              class="btn btn-primary flex-shrink"
              classList={{
                "btn-disabled": loading(),
              }}
              href={`/event-registration/?eventId=${eventId()}&signupMode=join-team`}
              onClick={() => {
                // TODO - consider moving these to on mount of registration wizard
                logEvent({
                  eventType: "action",
                  data: {
                    eventId: eventId().toString(),
                    action: "click-join-team",
                  },
                });
              }}
            >
              Join Team
            </a>
          </div>
        </Show>
      </Show>
    </div>
  );
};

export default GrandEventCard;
